import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Group,
  Title,
  Button,
  Paper,
  LoadingOverlay,
  Table,
  Badge,
  ActionIcon,
  Text,
  ThemeIcon,
  Modal,
  rem,
  SimpleGrid,
  TextInput,
  Center,
  Divider,
  Box,
  Select,
  Accordion,
  Tooltip,
  ScrollArea,
  Grid,
  MultiSelect,
} from '@mantine/core';
import {
  IconPlus,
  IconBuildingCommunity,
  IconBolt,
  IconDroplet,
  IconFlame,
  IconWifi,
  IconPhone,
  IconEdit,
  IconTrash,
  IconAlertTriangle,
  IconSearch,
  IconReceipt2,
  IconCircleCheck,
  IconCircleX,
  IconFilter,
  IconX,
  IconDownload,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import api from '../../api/axiosConfig';
import AddSubscriptionModal from '../../components/Subscriptions/AddSubscriptionModal';

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  const [opened, { open, close }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const [selectedRecord, setSelectedRecord] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState([]); // 🔥 array oldu
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('branch_az');

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/subscriptions/');
      setSubscriptions(response.data || []);
    } catch (error) {
      console.error('Abonelikler çekilemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Ekranda görünen satırların ID'lerini gönderiyoruz. Böylece Excel'de
  // mevcut arama, çoklu şube, tür, durum ve sıralama birebir korunur.
  // `all` modu ise veritabanındaki tüm abonelikleri sunucudan çeker.
  const handleExportExcel = async (mode = 'filtered') => {
    if (exportLoading) return;
    const ids = mode === 'filtered' ? filteredSubscriptions.map((s) => s.id) : null;
    if (mode === 'filtered' && ids.length === 0) return;

    setExportLoading(true);
    try {
      const response = await api.post(
        '/subscriptions/export/excel',
        { subscription_ids: ids },
        { responseType: 'blob', timeout: 120000 }
      );

      const contentType = response.headers?.['content-type'] || '';
      if (!contentType.includes('spreadsheetml')) {
        throw new Error('Sunucu geçerli bir Excel dosyası döndürmedi.');
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `smsgrup_abonelik_${mode === 'filtered' ? 'filtreli_' : 'tum_'}${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      let message = 'Excel raporu indirilemedi. Lütfen tekrar deneyin.';
      // responseType: blob olduğundan hata mesajı da Blob olarak dönebilir.
      if (error.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await error.response.data.text());
          if (typeof payload.detail === 'string') message = payload.detail;
        } catch (_) { /* Sunucunun varsayılan hata mesajını göster. */ }
      } else if (error.message && !error.response) {
        message = error.message;
      }
      window.alert(message);
    } finally {
      setExportLoading(false);
    }
  };

  const getTypeInfo = (type) => {
    switch (type) {
      case 'Elektrik':
        return { icon: IconBolt, color: 'yellow' };
      case 'Su':
        return { icon: IconDroplet, color: 'blue' };
      case 'Doğalgaz':
        return { icon: IconFlame, color: 'orange' };
      case 'İnternet':
        return { icon: IconWifi, color: 'teal' };
      case 'Telefon / Santral':
        return { icon: IconPhone, color: 'grape' };
      default:
        return { icon: IconBuildingCommunity, color: 'gray' };
    }
  };

  const branchOptions = useMemo(() => {
    const branches = Array.from(
      new Set(subscriptions.map((s) => s.branch_name || 'Diğer'))
    ).sort((a, b) => a.localeCompare(b, 'tr'));
    return branches.map((branch) => ({ value: branch, label: branch }));
  }, [subscriptions]);

  const typeOptions = useMemo(() => {
    const types = Array.from(
      new Set(subscriptions.map((s) => s.sub_type || 'Diğer'))
    ).sort((a, b) => a.localeCompare(b, 'tr'));
    return types.map((type) => ({ value: type, label: type }));
  }, [subscriptions]);

  const clearFilters = () => {
    setSearchTerm('');
    setBranchFilter([]); // 🔥 reset array
    setTypeFilter('all');
    setStatusFilter('all');
    setSortBy('branch_az');
  };

  const filteredSubscriptions = useMemo(() => {
    const q = searchTerm.trim().toLocaleLowerCase('tr-TR');

    let data = subscriptions.filter((sub) => {
      const matchesSearch =
        !q ||
        (sub.branch_name || '').toLocaleLowerCase('tr-TR').includes(q) ||
        (sub.sub_type || '').toLocaleLowerCase('tr-TR').includes(q) ||
        (sub.provider || '').toLocaleLowerCase('tr-TR').includes(q) ||
        (sub.subscription_no || '').toLocaleLowerCase('tr-TR').includes(q) ||
        (sub.notes || '').toLocaleLowerCase('tr-TR').includes(q);

      const matchesBranch =
        branchFilter.length === 0 ||
        branchFilter.includes(sub.branch_name || 'Diğer');

      const matchesType =
        typeFilter === 'all' || (sub.sub_type || 'Diğer') === typeFilter;

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && sub.is_active) ||
        (statusFilter === 'inactive' && !sub.is_active);

      return matchesSearch && matchesBranch && matchesType && matchesStatus;
    });

    data = [...data].sort((a, b) => {
      switch (sortBy) {
        case 'branch_az':
          return (a.branch_name || '').localeCompare(b.branch_name || '', 'tr');
        case 'branch_za':
          return (b.branch_name || '').localeCompare(a.branch_name || '', 'tr');
        case 'type_az':
          return (a.sub_type || '').localeCompare(b.sub_type || '', 'tr');
        case 'provider_az':
          return (a.provider || '').localeCompare(b.provider || '', 'tr');
        case 'newest':
          return new Date(b.start_date || 0) - new Date(a.start_date || 0);
        case 'oldest':
          return new Date(a.start_date || 0) - new Date(b.start_date || 0);
        default:
          return 0;
      }
    });

    return data;
  }, [subscriptions, searchTerm, branchFilter, typeFilter, statusFilter, sortBy]);

  const groupedData = useMemo(() => {
    const groups = {};
    filteredSubscriptions.forEach((sub) => {
      const branch = sub.branch_name || 'Diğer';
      if (!groups[branch]) groups[branch] = [];
      groups[branch].push(sub);
    });

    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b, 'tr'))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  }, [filteredSubscriptions]);

  const branchNames = Object.keys(groupedData);

  const summaryStats = useMemo(() => {
    const total = filteredSubscriptions.length;
    const active = filteredSubscriptions.filter((s) => s.is_active).length;
    const passive = filteredSubscriptions.filter((s) => !s.is_active).length;
    const branchCount = new Set(
      filteredSubscriptions.map((s) => s.branch_name || 'Diğer')
    ).size;

    return { total, active, passive, branchCount };
  }, [filteredSubscriptions]);

  const hasActiveFilters =
    searchTerm ||
    branchFilter.length > 0 || // 🔥 kritik değişiklik
    typeFilter !== 'all' ||
    statusFilter !== 'all' ||
    sortBy !== 'branch_az';

  const handleAddClick = () => {
    setSelectedRecord(null);
    open();
  };

  const handleEditClick = (record) => {
    setSelectedRecord(record);
    open();
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (!selectedRecord) return;

    setLoading(true);
    try {
      await api.delete(`/subscriptions/${selectedRecord.id}`);
      await fetchSubscriptions();
      closeDeleteModal();
      setSelectedRecord(null);
    } catch (error) {
      console.error('Silme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap="lg" pos="relative" style={{ minHeight: '70vh' }}>
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />

      {/* HERO / HEADER */}
      <Paper
        radius="xl"
        p={{ base: 'lg', md: 'xl' }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 40%, rgba(79,70,229,0.90) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: -70,
            right: -70,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(99,102,241,0.22)',
          }}
        />

        <Group
          justify="space-between"
          align="flex-start"
          wrap="wrap"
          style={{ position: 'relative', zIndex: 2 }}
        >
          <Stack gap={8}>
            <Group gap="sm">
              <ThemeIcon
                size={52}
                radius="xl"
                variant="black"
                color="rgba(255,255,255,0.12)"
              >
                <IconBuildingCommunity size={28} color="white" />
              </ThemeIcon>

              <div>
                <Text fw={700} size="sm" c="rgba(255,255,255,0.75)">
                  Kurumsal Hizmet Takibi
                </Text>
                <Title order={2} c="white" fw={900} style={{ letterSpacing: '-0.7px' }}>
                  Abonelik Yönetimi
                </Title>
              </div>
            </Group>

            <Text
              size="sm"
              maw={720}
              style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}
            >
              Şubelere ait elektrik, su, doğalgaz, internet ve telefon gibi tüm
              abonelik kayıtlarını tek ekrandan düzenli ve hızlı biçimde yönetin.
            </Text>
          </Stack>

          <Group gap="sm" wrap="wrap">
            <Button
              leftSection={<IconFileSpreadsheet size={18} />}
              onClick={() => handleExportExcel('filtered')}
              loading={exportLoading}
              disabled={loading || filteredSubscriptions.length === 0}
              radius="xl"
              size="md"
              variant="light"
              color="gray"
              styles={{ root: { backgroundColor: 'rgba(255,255,255,0.14)', color: '#fff' } }}
            >
              Excel İndir ({filteredSubscriptions.length})
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleAddClick}
              radius="xl"
              size="md"
              variant="white"
              color="dark"
            >
              Yeni Abonelik
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* ÖZET KARTLAR */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        <Paper
          radius="xl"
          p="lg"
          withBorder
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>
                Görünen Kayıt
              </Text>
              <Title order={2} fw={900}>
                {summaryStats.total}
              </Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="indigo">
              <IconReceipt2 size={26} />
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper
          radius="xl"
          p="lg"
          withBorder
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>
                Aktif
              </Text>
              <Title order={2} fw={900}>
                {summaryStats.active}
              </Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="green">
              <IconCircleCheck size={26} />
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper
          radius="xl"
          p="lg"
          withBorder
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>
                Pasif
              </Text>
              <Title order={2} fw={900}>
                {summaryStats.passive}
              </Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="red">
              <IconCircleX size={26} />
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper
          radius="xl"
          p="lg"
          withBorder
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed" fw={600}>
                Şube
              </Text>
              <Title order={2} fw={900}>
                {summaryStats.branchCount}
              </Title>
            </div>
            <ThemeIcon size={52} radius="xl" variant="light" color="blue">
              <IconBuildingCommunity size={26} />
            </ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      {/* FİLTRE ALANI */}
      <Paper
        radius="xl"
        withBorder
        p="lg"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
        }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <div>
              <Title order={4} fw={800}>
                Liste ve Filtreler
              </Title>
              <Text size="sm" c="dimmed">
                Şube, abonelik türü, durum ve arama ile kayıtları şube bazlı takip et.
              </Text>
            </div>

            <Group gap="xs">
              <Button
                variant="light"
                radius="xl"
                leftSection={<IconX size={16} />}
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                Filtreleri Temizle
              </Button>

              <Tooltip label="Filtrelerden bağımsız, veritabanındaki bütün abonelikleri indirir" withArrow>
                <Button
                  variant="light"
                  color="teal"
                  radius="xl"
                  leftSection={<IconDownload size={16} />}
                  onClick={() => handleExportExcel('all')}
                  loading={exportLoading}
                  disabled={loading || subscriptions.length === 0}
                >
                  Tüm Abonelikleri İndir
                </Button>
              </Tooltip>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleAddClick}
                radius="xl"
              >
                Yeni Abonelik
              </Button>
            </Group>
          </Group>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                placeholder="Şube, sağlayıcı, abonelik no veya not ara..."
                leftSection={<IconSearch size={16} />}
                radius="xl"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <MultiSelect
                leftSection={<IconFilter size={16} />}
                radius="xl"
                placeholder="Şube seç"
                data={branchOptions}
                value={branchFilter}
                onChange={setBranchFilter}
                searchable
                clearable
                nothingFoundMessage="Şube bulunamadı"
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Select
                leftSection={<IconFilter size={16} />}
                radius="xl"
                placeholder="Tür"
                data={[{ value: 'all', label: 'Tüm Türler' }, ...typeOptions]}
                value={typeFilter}
                onChange={(value) => setTypeFilter(value || 'all')}
                clearable={false}
              />
            </Grid.Col>
          </Grid>

          <Divider />

          {branchNames.length > 0 ? (
            <Accordion
              multiple
              variant="separated"
              radius="xl"
              defaultValue={branchNames.slice(0, 1)}
            >
              {branchNames.map((branch) => {
                const items = groupedData[branch];
                const activeCount = items.filter((i) => i.is_active).length;
                const passiveCount = items.length - activeCount;

                return (
                  <Accordion.Item value={branch} key={branch}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="nowrap" pr="md">
                        <Group gap="sm" wrap="nowrap">
                          <ThemeIcon size={36} radius="xl" variant="light" color="indigo">
                            <IconBuildingCommunity size={18} />
                          </ThemeIcon>

                          <div>
                            <Text fw={800} size="sm">
                              {branch}
                            </Text>
                          </div>
                        </Group>

                        <Group gap={8} wrap="nowrap">
                          <Badge radius="xl" variant="light" color="indigo">
                            {items.length} kayıt
                          </Badge>
                          <Badge radius="xl" variant="light" color="green">
                            {activeCount} aktif
                          </Badge>
                          <Badge radius="xl" variant="light" color="red">
                            {passiveCount} pasif
                          </Badge>
                        </Group>
                      </Group>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <Paper
                        radius="lg"
                        withBorder
                        style={{
                          overflowX: 'auto',
                          background: '#fff',
                        }}
                      >
                        <ScrollArea type="auto" offsetScrollbars>
                          <Table
                            striped
                            highlightOnHover
                            verticalSpacing="md"
                            horizontalSpacing="md"
                            style={{ minWidth: 980 }}
                          >
                            <Table.Thead
                              style={{
                                background:
                                  'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                              }}
                            >
                              <Table.Tr>
                                <Table.Th>Hizmet Türü</Table.Th>
                                <Table.Th>Kurum / Sağlayıcı</Table.Th>
                                <Table.Th>Abonelik / Tesisat No</Table.Th>
                                <Table.Th>Başlangıç Tarihi</Table.Th>
                                <Table.Th>Durum</Table.Th>
                                <Table.Th>Açıklama</Table.Th>
                                <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
                              </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                              {items.map((sub) => {
                                const { icon: TypeIcon, color: typeColor } = getTypeInfo(sub.sub_type);

                                return (
                                  <Table.Tr
                                    key={sub.id}
                                    style={{
                                      opacity: sub.is_active ? 1 : 0.72,
                                      transition: 'background 0.2s ease',
                                    }}
                                  >
                                    <Table.Td>
                                      <Group gap="sm" wrap="nowrap">
                                        <ThemeIcon
                                          variant="light"
                                          color={typeColor}
                                          size="lg"
                                          radius="xl"
                                        >
                                          <TypeIcon
                                            style={{ width: rem(16), height: rem(16) }}
                                          />
                                        </ThemeIcon>
                                        <div>
                                          <Text fw={700} size="sm">
                                            {sub.sub_type || '-'}
                                          </Text>
                                          <Text size="xs" c="dimmed">
                                            Hizmet kategorisi
                                          </Text>
                                        </div>
                                      </Group>
                                    </Table.Td>

                                    <Table.Td>
                                      <Text fw={600} size="sm">
                                        {sub.provider || '-'}
                                      </Text>
                                    </Table.Td>

                                    <Table.Td>
                                      <Badge
                                        variant="light"
                                        color="dark"
                                        radius="sm"
                                        styles={{
                                          label: {
                                            fontFamily:
                                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                            fontWeight: 700,
                                          },
                                        }}
                                      >
                                        {sub.subscription_no || '-'}
                                      </Badge>
                                    </Table.Td>

                                    <Table.Td>
                                      <Text size="sm">
                                        {sub.start_date
                                          ? dayjs(sub.start_date).format('DD.MM.YYYY')
                                          : '-'}
                                      </Text>
                                    </Table.Td>

                                    <Table.Td>
                                      <Badge
                                        color={sub.is_active ? 'green' : 'red'}
                                        variant="light"
                                        radius="xl"
                                      >
                                        {sub.is_active ? 'Aktif' : 'İptal Edildi'}
                                      </Badge>
                                    </Table.Td>

                                    <Table.Td>
                                      <Text
                                        size="sm"
                                        c="dimmed"
                                        lineClamp={2}
                                        style={{ maxWidth: 260 }}
                                      >
                                        {sub.notes || '-'}
                                      </Text>
                                    </Table.Td>

                                    <Table.Td>
                                      <Group gap={6} justify="flex-end" wrap="nowrap">
                                        <Tooltip label="Düzenle" withArrow>
                                          <ActionIcon
                                            variant="light"
                                            color="blue"
                                            radius="xl"
                                            size="lg"
                                            onClick={() => handleEditClick(sub)}
                                          >
                                            <IconEdit size={18} />
                                          </ActionIcon>
                                        </Tooltip>

                                        <Tooltip label="Sil" withArrow>
                                          <ActionIcon
                                            variant="light"
                                            color="red"
                                            radius="xl"
                                            size="lg"
                                            onClick={() => handleDeleteClick(sub)}
                                          >
                                            <IconTrash size={18} />
                                          </ActionIcon>
                                        </Tooltip>
                                      </Group>
                                    </Table.Td>
                                  </Table.Tr>
                                );
                              })}
                            </Table.Tbody>
                          </Table>
                        </ScrollArea>
                      </Paper>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : (
            <Paper radius="xl" withBorder p="xl" ta="center">
              <Center>
                <Stack align="center" gap="sm" py="xl">
                  <ThemeIcon size={72} radius="xl" variant="light" color="gray">
                    <IconBuildingCommunity size={34} />
                  </ThemeIcon>
                  <Title order={4}>Sonuç bulunamadı</Title>
                  <Text c="dimmed" size="sm" maw={420}>
                    Uyguladığın filtrelere uyan abonelik kaydı yok. Filtreleri temizleyip
                    tekrar bak.
                  </Text>
                  <Group gap="sm" mt="xs">
                    <Button variant="light" radius="xl" onClick={clearFilters}>
                      Filtreleri Temizle
                    </Button>
                    <Button
                      leftSection={<IconPlus size={16} />}
                      onClick={handleAddClick}
                      radius="xl"
                    >
                      Yeni Abonelik Ekle
                    </Button>
                  </Group>
                </Stack>
              </Center>
            </Paper>
          )}
        </Stack>
      </Paper>

      {/* EKLE / DÜZENLE MODALI */}
      <AddSubscriptionModal
        opened={opened}
        close={close}
        onSuccess={fetchSubscriptions}
        recordToEdit={selectedRecord}
      />

      {/* SİLME MODALI */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        radius="xl"
        title={
          <Group gap="sm">
            <ThemeIcon color="red" variant="light" radius="xl">
              <IconAlertTriangle size={18} />
            </ThemeIcon>
            <Text fw={800}>Abonelik Silme</Text>
          </Group>
        }
      >
        <Stack gap="lg">
          <Paper
            p="md"
            radius="lg"
            style={{
              background: 'var(--mantine-color-red-0)',
              border: '1px solid var(--mantine-color-red-2)',
            }}
          >
            <Text size="sm">
              Bu abonelik kaydını sistemden silmek istediğinize emin misiniz?
              Bu işlem geri alınamaz.
            </Text>
          </Paper>

          {selectedRecord && (
            <Paper withBorder p="md" radius="lg">
              <Stack gap={6}>
                <Text size="sm">
                  <b>Şube:</b> {selectedRecord.branch_name || '-'}
                </Text>
                <Text size="sm">
                  <b>Hizmet Türü:</b> {selectedRecord.sub_type || '-'}
                </Text>
                <Text size="sm">
                  <b>Sağlayıcı:</b> {selectedRecord.provider || '-'}
                </Text>
                <Text size="sm">
                  <b>Abonelik No:</b> {selectedRecord.subscription_no || '-'}
                </Text>
              </Stack>
            </Paper>
          )}

          <Group justify="flex-end">
            <Button variant="default" radius="xl" onClick={closeDeleteModal}>
              İptal
            </Button>
            <Button color="red" radius="xl" onClick={confirmDelete}>
              Evet, Sil
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

